// Thin fetch wrapper for the read-plane HTTP API v2.
//
// Base URL is `https://api.file-viewer.aldelo.ai` in production and is
// overridable at build time via `VITE_API_BASE_URL` for preview builds
// that need to point at a different stage.

import { isRenderHint, type FileMetadata, type ServerError } from './types';

const API_BASE_URL: string =
  (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ??
  'https://api.file-viewer.aldelo.ai';

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly correlationId: string | undefined;

  public constructor(status: number, code: string, message: string, correlationId?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.correlationId = correlationId;
  }
}

interface Envelope<T> {
  error?: ServerError;
  data?: T;
}

function assertMetadataShape(raw: unknown): FileMetadata {
  if (!raw || typeof raw !== 'object') {
    throw new ApiError(502, 'BAD_GATEWAY', 'metadata response is not an object');
  }
  const obj = raw as Record<string, unknown>;
  const filename = obj['filename'];
  const size = obj['size_bytes'];
  const contentType = obj['content_type'];
  const renderHint = obj['render_hint'];
  const expiresAt = obj['expires_at'];
  const contentUrl = obj['content_url'];

  if (typeof filename !== 'string') {
    throw new ApiError(502, 'BAD_GATEWAY', 'filename missing from metadata');
  }
  if (typeof size !== 'number') {
    throw new ApiError(502, 'BAD_GATEWAY', 'size_bytes missing from metadata');
  }
  if (typeof contentType !== 'string') {
    throw new ApiError(502, 'BAD_GATEWAY', 'content_type missing from metadata');
  }
  if (!isRenderHint(renderHint)) {
    throw new ApiError(
      502,
      'BAD_GATEWAY',
      `unknown render_hint from server: ${String(renderHint)}`,
    );
  }
  if (typeof expiresAt !== 'string') {
    throw new ApiError(502, 'BAD_GATEWAY', 'expires_at missing from metadata');
  }
  if (typeof contentUrl !== 'string' || !contentUrl.startsWith('https://')) {
    throw new ApiError(502, 'BAD_GATEWAY', 'content_url missing or not https');
  }

  return {
    filename,
    size_bytes: size,
    content_type: contentType,
    render_hint: renderHint,
    expires_at: expiresAt,
    content_url: contentUrl,
  };
}

/**
 * Fetch metadata + presigned content URL for a token.
 *
 * Rejects with `ApiError` on non-2xx. Callers should check `.status`:
 * - 404 → token unknown, expired, or pending
 * - 410 → ready row but S3 object gone (lazy-delete fired)
 * - 5xx → server failure, show generic error
 */
export async function fetchMetadata(token: string, signal?: AbortSignal): Promise<FileMetadata> {
  const url = `${API_BASE_URL}/api/v1/files/${encodeURIComponent(token)}`;
  let resp: Response;
  try {
    resp = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: signal ?? null,
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') {
      throw err;
    }
    throw new ApiError(0, 'NETWORK_ERROR', `network failure: ${(err as Error).message}`);
  }

  const correlationId = resp.headers.get('X-Correlation-Id') ?? undefined;
  let body: Envelope<unknown>;
  try {
    body = (await resp.json()) as Envelope<unknown>;
  } catch {
    throw new ApiError(
      resp.status,
      'BAD_GATEWAY',
      `non-JSON response (status ${resp.status})`,
      correlationId,
    );
  }

  if (!resp.ok) {
    const errObj = body.error;
    const code = errObj?.code ?? 'UNKNOWN_ERROR';
    const message = errObj?.message ?? `server returned status ${resp.status}`;
    throw new ApiError(resp.status, code, message, correlationId);
  }

  return assertMetadataShape(body.data);
}

/** Build the `/download` URL for this token (302 to presigned S3 GET). */
export function buildDownloadUrl(token: string): string {
  return `${API_BASE_URL}/api/v1/files/${encodeURIComponent(token)}/download`;
}

/** Parse the token out of a path like `/v/abc123`. Returns null if malformed. */
export function tokenFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/v\/([A-Za-z0-9_-]{16,64})\/?$/);
  return match && match[1] ? match[1] : null;
}
